import React, { Fragment, useState, useEffect } from 'react';
import { Input, Button } from 'reactstrap';
import { ModalButtonBuilder, ButtonBuilder, ToastBuilder, ApiRequest, CommonModal } from '../../../../../../../../GlobalComponents/Builders';
import { showRelativeTime, handleTabOnKeyDown } from '../../../../../../../../GlobalComponents/Helpers';
import useConfirmModal from '../../../../../../../../GlobalComponents/Builders/ConfirmModal';

/**
 * JobNotesSection — Manage per-job notes stored in dmt_job_setting with type='note'.
 *
 * Notes are human-only context (instructions, gotchas, changelog) and are NOT
 * loaded into args['global'] during processing.
 */

const JobNotesSection = ({ jobData, isAdmin, isArchived, trigger }) => {

    const { confirm, ConfirmModal } = useConfirmModal();

    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);

    // Shared form state for both create and edit
    const [formTitle, setFormTitle] = useState('');
    const [formContent, setFormContent] = useState('');
    const [editingId, setEditingId] = useState(null);

    const jobId = jobData?.id || jobData?.job_id;

    const fetchNotes = () => {
        setLoading(true);
        ApiRequest('GET', '/dmt/jobs/{jobId}/globalSettings', {
            pathParams: { jobId },
            queryParams: { table: 'dmt_job_setting', conditions: '`type` = "note"' },
        }).then((res) => {
            if (res.success && Array.isArray(res.data)) {
                setNotes(res.data);
            } else {
                setNotes([]);
            }
            setLoading(false);
        });
    };

    useEffect(() => {
        if (jobId) fetchNotes();
    }, [jobId]);

    const handleCreate = () => {
        const title = formTitle.trim();
        const content = formContent.trim();
        if (!content) return ToastBuilder('error', 'Note content is required');

        ApiRequest('POST', '/dmt/jobs/{jobId}/globalSettings', {
            pathParams: { jobId },
            body: {
                job_id: parseInt(jobId),
                key: title || `note_${Date.now()}`,
                value: content,
                type: 'note',
                description: null,
            },
            queryParams: { table: 'dmt_job_setting' },
        }).then((res) => {
            if (res.success) {
                ToastBuilder('success', 'Note added');
                resetForm();
                fetchNotes();
            } else {
                ToastBuilder('error', 'Failed to create note');
            }
        });
    };

    const handleUpdate = () => {
        const title = formTitle.trim();
        const content = formContent.trim();
        if (!content) return ToastBuilder('error', 'Note content is required');

        ApiRequest('PATCH', '/dmt/jobs/{jobId}/globalSettings/{settingId}', {
            pathParams: { jobId, settingId: editingId },
            body: [
                { op: 'replace', path: 'key', value: title || `note_${Date.now()}` },
                { op: 'replace', path: 'value', value: content },
            ],
            queryParams: { table: 'dmt_job_setting' },
        }).then((res) => {
            if (res.success) {
                ToastBuilder('success', 'Note updated');
                resetForm();
                fetchNotes();
            } else {
                ToastBuilder('error', 'Failed to update note');
            }
        });
    };

    const handleDelete = async (settingId) => {
        const confirmed = await confirm('Delete this note? This cannot be undone.', { confirmLabel: 'Delete', title: 'Delete Note' });
        if (!confirmed) return;
        ApiRequest('DELETE', '/dmt/jobs/{jobId}/globalSettings/{settingId}', {
            pathParams: { jobId, settingId },
            queryParams: { table: 'dmt_job_setting', hardDelete: true },
        }).then((res) => {
            if (res.success) {
                ToastBuilder('success', 'Note deleted');
                if (editingId === settingId) resetForm();
                fetchNotes();
            } else {
                ToastBuilder('error', 'Failed to delete note');
            }
        });
    };

    const startEdit = (note) => {
        setEditingId(note.setting_id);
        setFormTitle(note.key && !note.key.startsWith('note_') ? note.key : '');
        setFormContent(note.value || '');
    };

    const resetForm = () => {
        setEditingId(null);
        setFormTitle('');
        setFormContent('');
    };

    const handleSubmit = () => {
        if (editingId) {
            handleUpdate();
        } else {
            handleCreate();
        }
    };

    const [modal, setModal] = useState(false);

    const openModal = () => {
        fetchNotes();
        setModal(true);
    };

    const modalBody = (
        <div>
            <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                Notes are for human context only — instructions, gotchas, changelog entries. They are not used during processing.
            </p>

            {/* Existing notes */}
            {loading ? (
                <p style={{ textAlign: 'center', padding: 20, color: '#999' }}>Loading...</p>
            ) : notes.length === 0 ? (
                <p style={{ textAlign: 'center', padding: 20, color: '#999' }}>No notes for this job yet.</p>
            ) : (
                <div style={{ marginBottom: 16, maxHeight: 300, overflowY: 'auto' }}>
                    {notes.map((note) => (
                        <div key={note.setting_id} className={editingId === note.setting_id ? 'b-l-primary' : ''} style={{
                            padding: '10px 12px', marginBottom: 8,
                            borderLeft: editingId === note.setting_id ? undefined : '3px solid rgba(128,128,128,0.2)',
                            background: editingId === note.setting_id ? 'rgba(36,105,92,0.04)' : 'rgba(128,128,128,0.02)',
                            borderRadius: 2
                        }}>
                            <div className='d-flex justify-content-between align-items-start'>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    {note.key && !note.key.startsWith('note_') && (
                                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{note.key}</div>
                                    )}
                                    <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#333' }}>
                                        {typeof note.value === 'object' ? JSON.stringify(note.value, null, 2) : note.value}
                                    </div>
                                    {note.created_at && (
                                        <div style={{ fontSize: 10, color: '#999', marginTop: 4 }}>
                                            {showRelativeTime(note.created_at)}
                                        </div>
                                    )}
                                </div>
                                {!isArchived && (
                                    <div className='d-flex gap-1' style={{ flexShrink: 0, marginLeft: 8 }}>
                                        <Button size='sm' color='link' className='p-0 txt-warning' style={{ fontSize: 11 }} onClick={() => startEdit(note)}>Edit</Button>
                                        <Button size='sm' color='link' className='p-0 text-danger' style={{ fontSize: 11 }} onClick={() => handleDelete(note.setting_id)}>Del</Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add / Edit note — single form */}
            {!isArchived && (
                <div style={{ borderTop: '1px solid rgba(128,128,128,0.2)', paddingTop: 12 }}>
                    <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{editingId ? 'Edit Note' : 'Add Note'}</span>
                        {editingId && (
                            <Button size='sm' color='link' className='p-0 txt-secondary' style={{ fontSize: 11 }} onClick={resetForm}>Cancel Edit</Button>
                        )}
                    </div>
                    <Input bsSize='sm' value={formTitle} onChange={(e) => setFormTitle(e.target.value)}
                        placeholder='Title (optional)' style={{ marginBottom: 8 }} />
                    <Input type='textarea' value={formContent} onChange={(e) => setFormContent(e.target.value)}
                        onKeyDown={handleTabOnKeyDown}
                        placeholder='Write your note here...' style={{ minHeight: 300, fontSize: 13, marginBottom: 8 }} />
                    <div className='d-flex justify-content-end'>
                        <ButtonBuilder form='' label={editingId ? 'Save Note' : 'Add Note'} className={`btn-${editingId ? 'success' : 'primary'} btn-sm px-3`}
                            onClick={handleSubmit} useSpinner={false} />
                    </div>
                </div>
            )}
        </div>
    );

    if (trigger) {
        return (
            <Fragment>
                <span onClick={openModal} style={{ display: 'inline-flex' }}>{trigger}</span>
                <CommonModal isOpen={modal} title='Job Notes' toggler={() => setModal(false)} size='lg' onSubmit={false} onCancelLabel='Close'>
                    {modalBody}
                </CommonModal>
                <ConfirmModal />
            </Fragment>
        );
    }

    return (
        <Fragment>
            <ModalButtonBuilder
                title='Job Notes'
                className='btn-outline-secondary'
                onSubmit={false}
                onCancelLabel='Close'
                size='lg'
                onClick={fetchNotes}
                body={modalBody}
            />
            <ConfirmModal />
        </Fragment>
    );
};

export default JobNotesSection;
